import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Lock, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { PhoneVerify } from "@/components/PhoneVerify";
import { TOOLKIT, TOOLKIT_ROLES, TOOLKIT_YEARS, TOOLKIT_GOALS } from "@/data/offers";
import { API, errorText } from "@/lib/api";
import { trackConversion } from "@/lib/track";
import { useDiagnostic } from "@/components/landing/DiagnosticContext";
import { TOOL_ICONS } from "@/components/landing/WhatYouGet";

const inputCls = "mt-1 h-11 w-full rounded-lg border border-slate-300 px-3 text-[14px] outline-none transition-colors focus:border-[#0E9384] focus:ring-2 focus:ring-teal-100";

const Pick = ({ label, value, onChange, options, testId }) => (
  <label className="block">
    <span className="text-[13px] font-semibold text-slate-700">{label} *</span>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="mt-1 h-11 border-slate-300 text-[14px]"><SelectValue placeholder={`Select your ${label.toLowerCase()}`} /></SelectTrigger>
      <SelectContent data-lenis-prevent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  </label>
);

const ToolList = () => (
  <ul className="space-y-2">
    {TOOLKIT.map((t) => {
      const { I, c } = TOOL_ICONS[t.icon];
      return (
        <li key={t.key} className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2 text-[13.5px] font-medium text-[#0B1B33]">
          <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-md ${c}`}><I className="h-4 w-4" /></span>{t.title}
        </li>
      );
    })}
  </ul>
);

export const ToolkitPopup = () => {
  const { toolkitOpen, closeToolkit, openToolkit, isOpen } = useDiagnostic();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", role: "", years: "", goal: "", consent_marketing: false });
  const [ph, setPh] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (sessionStorage.getItem("aor_popup_seen") || localStorage.getItem("aor_toolkit_done")) return undefined;
    const fire = () => { if (!document.querySelector("[role=dialog]")) openToolkit(); cleanup(); };
    const t = setTimeout(fire, 30000);
    const onLeave = (e) => { if (e.clientY <= 0) fire(); };
    const onScroll = () => { if (window.scrollY > document.body.scrollHeight * 0.55) fire(); };
    document.addEventListener("mouseout", onLeave);
    window.addEventListener("scroll", onScroll, { passive: true });
    function cleanup() { clearTimeout(t); document.removeEventListener("mouseout", onLeave); window.removeEventListener("scroll", onScroll); }
    return cleanup;
  }, [openToolkit, isOpen]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.role || !f.years || !f.goal) return toast.error("Please complete all required fields.");
    if (!ph) return toast.error("Please verify your mobile number first.");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/toolkit`, { ...f, phone: ph.phone, phone_token: ph.token });
      trackConversion("toolkit", { transaction_id: `toolkit-${data?.id}` });
      localStorage.setItem("aor_toolkit_done", "1");
      localStorage.setItem("aor_contact", JSON.stringify({ name: data?.name ?? f.name, email: data?.email ?? f.email }));
      if (data?.toolkit_path) localStorage.setItem("aor_toolkit_path", data.toolkit_path);
      closeToolkit();
      nav("/thank-you");
    } catch (err) {
      toast.error(errorText(err));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={toolkitOpen} onOpenChange={(o) => !o && closeToolkit()}>
      <DialogContent data-testid="toolkit-popup" data-lenis-prevent className="max-h-[94vh] w-[calc(100vw-20px)] max-w-4xl overflow-y-auto rounded-2xl p-0 [&>button]:hidden">
        <button data-testid="toolkit-popup-close-btn" onClick={closeToolkit} className="absolute right-4 top-4 z-10 rounded-full p-1.5 text-slate-600 hover:bg-slate-100" aria-label="Close"><X className="h-5 w-5" /></button>
        <div className="px-6 pb-2 pt-7 text-center md:px-10">
          <DialogTitle className="font-display text-[26px] font-extrabold tracking-tight text-[#0B1B33] md:text-[34px]">Get Your <span className="text-[#0E9384]">Free AI Project Toolkit</span></DialogTitle>
          <DialogDescription className="mt-1 text-[15px] text-slate-600">Enter your details to get access to all resources.</DialogDescription>
        </div>
        <div className="grid gap-6 px-6 pb-7 md:grid-cols-[1fr_1.05fr] md:px-10">
          <div className="hidden md:block">
            <ToolList />
            <p className="font-hand mt-5 -rotate-3 text-center text-[22px] leading-tight text-[#0B1B33]">Everything you need to go from learning to landing, in one free pack!</p>
          </div>
          <div className="md:hidden flex flex-wrap gap-1.5">
            {TOOLKIT.map((t) => <span key={t.key} className="rounded-full bg-teal-50 px-2.5 py-1 text-[12px] font-medium text-teal-800">{t.title}</span>)}
          </div>
          <form data-testid="toolkit-form" onSubmit={submit} className="space-y-3">
            <label className="block"><span className="text-[13px] font-semibold text-slate-700">Full Name *</span>
              <input data-testid="toolkit-name-input" required maxLength={80} value={f.name} onChange={(e) => set("name")(e.target.value)} placeholder="Your full name" className={inputCls} /></label>
            <label className="block"><span className="text-[13px] font-semibold text-slate-700">Email Address *</span>
              <input data-testid="toolkit-email-input" required type="email" value={f.email} onChange={(e) => set("email")(e.target.value)} placeholder="yourname@gmail.com" className={inputCls} /></label>
            <PhoneVerify value={ph} onChange={setPh} testId="toolkit-phone" />
            <Pick label="Current Role" value={f.role} onChange={set("role")} options={TOOLKIT_ROLES} testId="toolkit-role-select" />
            <Pick label="Experience" value={f.years} onChange={set("years")} options={TOOLKIT_YEARS} testId="toolkit-years-select" />
            <Pick label="Primary Goal" value={f.goal} onChange={set("goal")} options={TOOLKIT_GOALS} testId="toolkit-goal-select" />
            <label className="flex gap-2.5 pt-1 text-[13px] text-slate-700">
              <Checkbox data-testid="toolkit-consent-checkbox" checked={f.consent_marketing} onCheckedChange={(v) => set("consent_marketing")(!!v)} className="mt-0.5 data-[state=checked]:border-[#0E9384] data-[state=checked]:bg-[#0E9384]" />
              Send me occasional updates about live sessions and cohorts. (Optional)
            </label>
            <button data-testid="toolkit-submit-btn" disabled={busy}
              className="btn-teal flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-[17px] font-semibold disabled:opacity-60">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Get My Free Toolkit <ArrowRight className="h-5 w-5" /></>}
            </button>
            <p className="flex items-center justify-center gap-1.5 text-[12.5px] text-slate-600"><Lock className="h-3.5 w-3.5" /> We respect your privacy. No spam. Unsubscribe anytime.</p>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
